import { storeToRefs } from 'pinia';

import type {
  LoginWithPasswordInput,
  RequestLoginCodeInput,
  SignUpStudentInput,
  VerifyLoginCodeInput,
  VerifyStudentInviteOtpCodeInput,
} from '~/graphql/generated/graphql';

import { AUTH_BROADCAST_CHANNEL, AUTH_LOGGED_OUT_MESSAGE } from '~/constants/auth';
import { EVENTS } from '~/constants/events';
import {
  adminRoleFragment,
  deleteSelfMutation,
  loginWithPasswordMutation,
  logoutMutation,
  requestLoginCodeMutation,
  requestStudentInviteOtpCodeMutation,
  signUpStudentMutation,
  studentRoleFragment,
  teacherRoleFragment,
  verifyLoginCodeMutation,
  verifyStudentInviteOtpCodeMutation,
} from '~/graphql/documents/core';
import { useFragment } from '~/graphql/generated/fragment-masking';
import { RoleContextType, UserType } from '~/graphql/generated/graphql';

export const useAuth = () => {
  const authStore = useAuthStore();
  const { cluster } = useConfigStore();

  const { currentSchoolId, guestId, authId } = storeToRefs(authStore);

  const { $urqlClient, $i18n, $analytics, $logErrorMessage } = useNuxtApp();

  const user = computed(() => authStore.user);

  const isLoggedIn = computed(() => {
    return !!user.value;
  });

  const requestLoginCode = async (data: RequestLoginCodeInput) => {
    return await $urqlClient.mutation(requestLoginCodeMutation, {
      input: data,
    });
  };

  // The IA flag can be targeted per student, so a sign-in or out asks it again for the next page.
  // Best effort: a failed re-ask leaves the old answer until the next load, and never fails the
  // sign-in. Imported on use, so loading auth does not also load the feature-flag modules, which
  // read useAuth.
  const reaskIaFlag = async () => {
    try {
      await (await import('~/composables/unified/useIaAudience')).reaskIaFlag();
    }
    catch (error) {
      $logErrorMessage('[auth] ia-enabled re-ask failed', { error });
    }
  };

  const completeLoginProcess = async () => {
    await authStore.fetchUser(true);
    await reaskIaFlag();
    const externalId = user.value?.externalId;
    if (externalId) {
      $analytics.identify(externalId);
    }
  };

  const verifyLoginCode = async (verifyLoginCodeData: VerifyLoginCodeInput) => {
    const res = await $urqlClient.mutation(verifyLoginCodeMutation, {
      input: verifyLoginCodeData,
    });

    if (res.error) {
      return { flow: null, hasMatchedInvite: false, accountCreated: false, error: res.error };
    }

    if (!res.data?.core.auth.verifyLoginCode?.flow) {
      return { flow: null, hasMatchedInvite: false, accountCreated: false, error: 'An unknown error occurred' };
    }

    return {
      flow: res.data.core.auth.verifyLoginCode.flow,
      hasMatchedInvite: res.data.core.auth.verifyLoginCode.hasMatchedInvite,
      accountCreated: res.data.core.auth.verifyLoginCode.accountCreated,
      error: null,
    };
  };

  const loginWithPassword = async (input: LoginWithPasswordInput) => {
    const res = await $urqlClient.mutation(loginWithPasswordMutation, { input });

    if (res.error) {
      return { flow: null, hasMatchedInvite: false, accountCreated: false, error: res.error };
    }

    if (!res.data?.core.auth.loginWithPassword.flow) {
      return { flow: null, hasMatchedInvite: false, accountCreated: false, error: 'An unknown error occurred' };
    }

    return {
      flow: res.data.core.auth.loginWithPassword.flow,
      hasMatchedInvite: res.data.core.auth.loginWithPassword.hasMatchedInvite,
      accountCreated: res.data.core.auth.loginWithPassword.accountCreated,
      error: null,
    };
  };

  const signUpStudent = async (input: SignUpStudentInput) => {
    const res = await $urqlClient.mutation(signUpStudentMutation, { input });
    if (res.error) {
      return { error: res.error, passwordChangeToken: null };
    }
    return { error: null, passwordChangeToken: res.data?.core?.user?.signUpStudent?.passwordChangeToken ?? null };
  };

  const requestInviteCode = async (inviteCode: string) => {
    authStore.reset();
    const { error } = await $urqlClient.mutation(requestStudentInviteOtpCodeMutation, { input: { code: inviteCode } });

    if (error) {
      return { error: getFirstErrorMessageFromCombinedErrors($i18n.t, error) };
    }

    return { error: null };
  };

  const verifyInviteCode = async ({ email, code, inviteCode }: VerifyStudentInviteOtpCodeInput) => {
    authStore.reset();
    const { data, error } = await $urqlClient.mutation(verifyStudentInviteOtpCodeMutation, {
      input: {
        email,
        code,
        inviteCode,
      },
    });

    if (error) {
      return {
        flow: null,
        error: getFirstErrorMessageFromCombinedErrors($i18n.t, error),
      };
    }

    if (!data?.core.org.school.verifyStudentInviteOtpCode?.flow) {
      return { flow: null, error: $i18n.t('general.errors.unknown') };
    }

    return {
      flow: data.core.org.school.verifyStudentInviteOtpCode.flow,
      error: null,
    };
  };

  /**
   * Everything the client keeps about the person who was signed in, gone in one call, local
   * storage included. Called once a provider login signs someone in, so a shared classroom
   * device never shows the last student's state under the next student's session. The
   * analytics identity is not client state: it resets only when a session ends (logout,
   * session expiry). The auth-marker cookie is the server's to set and clear
   * (AttachAuthMarkerCookie).
   */
  const clearClientSession = () => {
    authStore.reset();
    useCrossSitesConfig().clear();
    if (import.meta.client && typeof localStorage !== 'undefined') {
      localStorage.clear();
    }
  };

  const logout = async (redirect: boolean = true, redirectPath: string | null = null) => {
    const appConfig = usePlatformAppConfig(useRoute().path);

    if (useRoute().path.startsWith('/unified/')) {
      $analytics.track(EVENTS.UNIFIED_AUTH_LOGOUT);
    }
    else if (useRoute().path.startsWith('/educators/')) {
      $analytics.track(EVENTS.SCHOOLS_AUTH_LOGOUT);
    }

    if (redirect) {
      authStore.isloggingOut = true;
    }

    authStore.reset();

    await $urqlClient.mutation(logoutMutation, {});

    if (import.meta.client && typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      channel.postMessage(AUTH_LOGGED_OUT_MESSAGE);
      channel.close();
    }

    $analytics.reset();
    $analytics.setUserProperties({ isAnywayUser: false });
    useCrossSitesConfig().clear(); // extra clearing

    if (!redirect) {
      await reaskIaFlag();
      return;
    }

    // External nav strips the router base, so we always navigate to the
    // cluster-prefixed URL. If a caller passes a relative `redirectPath`,
    // resolve it through the current cluster's baseUrl too.
    const target = redirectPath ?? appConfig.logoutRedirectRoute;
    await navigateTo(clusterUrl(target), { replace: true, external: true });
  };

  const studentRoles = computed(() => {
    if (!user.value) {
      return [];
    }

    return user.value.studentRoles.map(r => useFragment(studentRoleFragment, r));
  });

  const adminRoles = computed(() => {
    if (!user.value) {
      return [];
    }

    return user.value.adminRoles.map(r => useFragment(adminRoleFragment, r));
  });

  const schoolStudentRoles = computed(() => {
    return studentRoles.value.filter(r => r.contextType === RoleContextType.Org);
  });

  const globalStudentRole = computed(() => {
    return studentRoles.value.find(r => r.contextType === RoleContextType.Global);
  });

  // The student role that holds the account's own details: the global row for a consumer student,
  // the school's row for a school account student.
  const accountStudentRole = computed(() => {
    if (!user.value?.accountStudentRole) {
      return undefined;
    }

    return useFragment(studentRoleFragment, user.value.accountStudentRole);
  });

  const userDisplayName = computed(() => {
    return [accountStudentRole.value?.firstName, accountStudentRole.value?.lastName]
      .filter(Boolean)
      .join(' ');
  });

  const schoolTeacherRoles = computed(() => {
    if (!user.value) {
      return [];
    }

    return user.value.teacherRoles.map(r => useFragment(teacherRoleFragment, r));
  });

  // A user "belongs to a school" when they hold an org-context student role, or an org-context
  // teacher role that is not their own Personal classroom. Used to gate features a school can
  // object to, e.g. coin cashout. Mirrors the backend's hasSchoolUserRole().
  const isSchoolUser = computed(() =>
    schoolStudentRoles.value.length > 0
    || schoolTeacherRoles.value.some(r => r.contextType === RoleContextType.Org && !r.isPersonalTeacherRole),
  );

  // The teacher row at the Personal classroom the user owns, in either context, falling back to
  // a legacy global teacher row. Mirrors the backend's personalTeacherRole().
  const personalTeacherRole = computed(() => {
    return schoolTeacherRoles.value.find(r => r.isPersonalTeacherRole)
      ?? schoolTeacherRoles.value.find(r => r.contextType === RoleContextType.Global);
  });

  const schoolOperationalRoles = computed(() => {
    const roles = schoolTeacherRoles.value;
    // sort roles by personal school
    return [
      ...roles.filter(role => role.school && !role.school.isPersonalSchool),
      ...(cluster?.manageRegisterEnabled ? roles.filter(role => role.school && role.school.isPersonalSchool) : []),
    ];
  });

  // The operational role for the school the user is currently acting in (undefined when none
  // matches). Single source of truth for "which school role is active right now".
  const currentSchoolRole = computed(() =>
    schoolOperationalRoles.value.find(role => role.orgId === currentSchoolId.value),
  );

  const currentSchool = computed(() => currentSchoolRole.value?.school);

  // The school a teacher acts for: the current one, else their Personal classroom, which is not an
  // operational school on a cluster without register management. Undefined for a legacy global
  // teacher row, which belongs to no school.
  const teacherSchoolId = computed(() => currentSchoolId.value ?? personalTeacherRole.value?.orgId ?? undefined);

  // Watching `schoolOperationalRoles` only (not `currentSchoolId`) — writing to
  // `currentSchoolId` here mustn't re-trigger this watcher. Vue 3.5.34 tightened
  // self-write detection on `watchEffect`; an explicit `watch` source avoids the
  // recursive-update loop.
  watch(
    schoolOperationalRoles,
    (roles) => {
      if (!authStore.currentSchoolId || !roles.find(r => r.orgId === authStore.currentSchoolId)) {
        authStore.currentSchoolId = roles[0]?.orgId ?? undefined;
      }
    },
    { immediate: true },
  );

  const switchToSchool = (schoolId: string) => {
    authStore.currentSchoolId = schoolId;
  };

  const deleteSelf = async (redirectPath: string = '/educators/account-deleted') => {
    const res = await useNuxtApp().$urqlClient.mutation(deleteSelfMutation, {});

    if (!res.data?.core.user.deleteSelf?.success) {
      return {
        success: false,
        error: res.error ? getFirstErrorMessageFromCombinedErrors($i18n.t, res.error) : $i18n.t('general.unexpectedError'),
      };
    }

    await logout(true, redirectPath);

    return {
      success: true,
      error: null,
    };
  };

  const isGhostStudent = computed(() => {
    return user?.value?.isGhostStudent;
  });

  const isGhostLearner = computed(() => {
    return user?.value?.isGhostLearner;
  });

  /**
   * Minted to do one anonymous lesson, and nothing else.
   *
   * Not interchangeable with the two above: those are true for a consumer ghost as well, and a
   * consumer ghost is someone who may still sign up and keep their work. This one never can, which
   * is why anything offering an account, or recording who did something, has to tell them apart.
   */
  const isLessonGhost = computed(() => user?.value?.type === UserType.LessonGhost);

  const isRegistered = computed(() => user?.value?.type === UserType.Registered);

  // The subtraction, named once: a ghost who may still sign up and keep their work, as opposed to
  // a lesson participant, who never can.
  const isConsumerGhost = computed(() => !!isGhostStudent.value && !isLessonGhost.value);

  const userHasNoTeacherRole = computed(() => authStore.isLoggedIn && !personalTeacherRole.value && !schoolTeacherRoles.value.length);
  const canCreateTeacherRole = computed(() => userHasNoTeacherRole.value && !!globalStudentRole.value);

  // Row-level gated on the graph (self-readable only), so it can come back null — an unknown
  // referee is not a referee.
  const isReferredUser = computed(() => user?.value?.isReferredUser ?? false);

  return {
    user,
    fetchUser: authStore.fetchUser,
    isLoggedIn,
    isGhostStudent,
    isGhostLearner,
    isLessonGhost,
    isConsumerGhost,
    isRegistered,
    isReferredUser,
    reset: authStore.reset,
    requestLoginCode,
    completeLoginProcess,
    clearClientSession,
    verifyLoginCode,
    loginWithPassword,
    signUpStudent,
    requestInviteCode,
    verifyInviteCode,
    logout,
    switchToSchool,
    studentRoles,
    adminRoles,
    schoolStudentRoles,
    isSchoolUser,
    schoolTeacherRoles,
    schoolOperationalRoles,
    currentSchoolRole,
    currentSchool,
    globalStudentRole,
    accountStudentRole,
    userDisplayName,
    personalTeacherRole,
    deleteSelf,
    currentSchoolId,
    teacherSchoolId,
    guestId,
    authId,
    userHasNoTeacherRole,
    canCreateTeacherRole,
  };
};
